import {
  APP_INITIALIZER,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';
import { appReducers } from './store/app.reducers';
import { appEffects } from './store/app.effects';
import { AuthService } from './auth/auth.service';
import { AuthGuardWithForcedLogin } from './auth/auth-guard-login.service';
import {
  AuthConfig,
  OAuthModule,
  OAuthModuleConfig,
  OAuthStorage,
} from 'angular-oauth2-oidc';
import { authAppInitializerFactory } from './auth/auth-app-initializer.factory';
import { authConfig, authModuleConfig } from './auth/auth-config';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AuthInterceptor } from './auth/auth.interceptor';
import { SessionModule } from './session/session.module';
import { SessionService } from './session/session.service';
import { UnauthorizedInterceptor } from './http/unauthorized.interceptor';
import { AUTH_CLIENT } from './session/session.tokens';
import { AuthClientAdapter } from './auth/auth-client.adapter';

// We need a factory since localStorage is not available at AOT build time
export function storageFactory(): OAuthStorage {
  return localStorage;
}

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    OAuthModule.forRoot(),
    SessionModule.forRoot({
      idleTimeoutMs: 45 * 60_000,
      warningBeforeMs: 60_000,
      checkIntervalMs: 1_000,
      activityThrottleMs: 1_000,
      persistStrategy: 'localStorage',
      storageKeyLastActivity: '__app.lastActivity',
      storageKeySessionId: '__app.sessionId',
      broadcastChannelName: 'atalef-session',
      crossTab: 'global',
      endSessionOn401: false,
      performRedirectOnLogout: true,
      postLogoutRedirectUri: '/',
    }),
    StoreModule.forRoot(appReducers, {}),
    EffectsModule.forRoot(appEffects),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
  ],
  providers: [AuthService, AuthGuardWithForcedLogin],
})
export class CoreModule {
  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        {
          provide: APP_INITIALIZER,
          useFactory: authAppInitializerFactory,
          deps: [AuthService],
          multi: true,
        },
        { provide: AuthConfig, useValue: authConfig },
        { provide: OAuthModuleConfig, useValue: authModuleConfig },
        { provide: OAuthStorage, useFactory: storageFactory },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        // Disabled to prevent redirect loops - existing AuthInterceptor handles 401
        // { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedInterceptor, multi: true },
        { provide: AUTH_CLIENT, useExisting: AuthClientAdapter },
      ],
    };
  }

  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error(
        'CoreModule is already loaded. Import it in the AppModule only',
      );
    }
  }
}
